import { Button } from '@app/components/various/Button';
import { Dropdown, DropdownMenu, DropdownTrigger } from '@app/components/various/Dropdown';
import { MoreVerticallySmall, Plus } from '@app/components/various/Icons';
import { Link } from '@app/components/various/Link';
import { Modal } from '@app/components/various/Modal';
import { Table } from '@app/components/various/Table';
import { BREAKPOINTS } from '@app/constants/breakpoints';
import { getIsBuyerEditingEnabled } from '@app/ducks';
import { useCreateBuyer } from '@app/services/hooks/account/useCreateBuyer';
import { useUpdateBuyer } from '@app/services/hooks/account/useUpdateBuyer';
import { useIsScreenSize } from '@app/utils/hooks';
import { useGetBuyerOptions } from '@app/utils/hooks/accounts/useGetBuyerOptions';
import { useTable } from '@app/utils/hooks/table/useTable';
import { isDefined } from '@app/utils/is';
import { renderSentryMaskedText } from '@app/utils/monitoring';
import { renderMaskedCell } from '@app/utils/table';
import { createColumnHelper, getCoreRowModel, getSortedRowModel } from '@tanstack/react-table';
import { Buyer } from '@typings';
import { pick } from 'ramda';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { BuyerForm } from './BuyerForm';
import { BuyersListCard } from './BuyersListCard';
import { BuyersListCardSkeleton } from './BuyersListCardSkeleton';

const columnHelper = createColumnHelper<Buyer.Base>();

interface Props {
  accountId: string;
  buyers: Buyer.Base[];
  title?: string;
  isLoading?: boolean;
}

export const BuyersList = ({ accountId, buyers, isLoading = false, title }: Props) => {
  const { t } = useTranslation(['buyers', 'common']);
  const isBuyerEditingEnabled = useSelector(getIsBuyerEditingEnabled);

  const [activeBuyerId, setActiveBuyerId] = React.useState('');
  const [isUpdateModalOpen, setIsUpdateModalOpen] = React.useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = React.useState(false);

  const isMobile = useIsScreenSize(BREAKPOINTS.SM);
  const isTablet = useIsScreenSize(BREAKPOINTS.MD);

  const { mutate: createBuyer, isPending: isCreatingBuyer } = useCreateBuyer();
  const { mutate: updateBuyer, isPending: isUpdatingBuyer } = useUpdateBuyer();

  const handleCreateBuyerSubmit = (data: Buyer.BuyerDTO) => {
    createBuyer(
      { accountId, data },
      {
        onSuccess: () => setIsCreateModalOpen(false),
      },
    );
  };

  const handleUpdateBuyerSubmit = (data: Buyer.BuyerDTO) => {
    updateBuyer(
      { accountId, buyerId: activeBuyerId, data },
      {
        onSuccess: () => {
          setIsUpdateModalOpen(false);
        },
      },
    );
  };

  const handleEdit = React.useCallback((buyerId: string) => {
    setIsUpdateModalOpen(true);
    setActiveBuyerId(buyerId);
  }, []);

  const getOptions = useGetBuyerOptions({
    accountId,
    buyers,
    onEdit: handleEdit,
  });

  const columns = React.useMemo(
    () => [
      columnHelper.accessor('firstName', {
        cell: ({ row }) => {
          const buyerFullName = `${row.original.firstName} ${row.original.lastName}`.trim();

          return (
            <button
              type="button"
              className="rounded-small absolute-cursor-pointer"
              data-sentry-mask
              aria-label={`${t('common:edit')} ${buyerFullName}`}
              onClick={() => handleEdit(row.original.buyer)}
            >
              {buyerFullName}
            </button>
          );
        },
        header: t('common:name'),
      }),
      columnHelper.accessor('email', {
        cell: ({ row }) => (
          <Link to={`mailto:${row.original.email}`} className="z-interactiveTableItem">
            {renderSentryMaskedText(row.original.email)}
          </Link>
        ),
        header: t('common:email'),
      }),
      columnHelper.accessor('phoneNumber', {
        cell: renderMaskedCell,
        header: t('common:phone'),
      }),
      columnHelper.accessor('mobileNumber', {
        cell: renderMaskedCell,
        header: t('common:mobile'),
      }),
      ...(isBuyerEditingEnabled ?
        [
          columnHelper.display({
            cell: ({ row }) => (
              <Dropdown title={t('common:option_other')} placement="bottom-end">
                <DropdownTrigger>
                  <Button
                    variant="text"
                    color="secondary"
                    aria-label={t('common:option_other')}
                    className="z-interactiveTableItem"
                    icon={MoreVerticallySmall}
                    onClick={() => setActiveBuyerId(row.original.buyer)}
                  />
                </DropdownTrigger>
                <DropdownMenu items={getOptions(row.original.buyer)} />
              </Dropdown>
            ),
            header: t('common:option_other'),
            id: 'options',
            meta: {
              align: 'center',
              width: 52,
            },
          }),
        ]
      : []),
    ],
    [getOptions, isBuyerEditingEnabled, t],
  );

  const table = useTable({
    columns,
    data: buyers,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const editedBuyerDefaultValues = React.useMemo(() => {
    const buyerData = buyers.find(buyer => buyer.buyer === activeBuyerId);

    if (!isDefined(buyerData)) {
      return;
    }

    return {
      ...pick(['email', 'firstName', 'lastName'], buyerData),
      mobileNumber: buyerData.mobileNumber ?? '',
      phoneNumber: buyerData.phoneNumber ?? '',
    };
  }, [buyers, activeBuyerId]);

  return (
    <>
      <div data-component="BuyersListTitle" className="mb-2 flex flex-wrap items-center justify-between gap-3">
        <h3 data-sentry-mask className="body-2">
          {title ?? t('buyers:buyer_other')}
          <span className="text-neutral-40"> ({buyers.length})</span>
        </h3>
        <Button
          variant="bordered"
          color="secondary"
          size={isMobile ? 'medium' : 'large'}
          isFluid={isMobile}
          disabled={isLoading}
          icon={Plus}
          onClick={() => setIsCreateModalOpen(true)}
        >
          {t('buyers:add_buyer')}
        </Button>
      </div>
      <Table
        table={table}
        isLoading={isLoading}
        variant="gray"
        skeletonRows={10}
        renderSkeletonCard={index => <BuyersListCardSkeleton key={index} />}
        renderCard={
          isTablet ?
            (row, index) => <BuyersListCard key={index} accountId={accountId} buyers={buyers} row={row} onEdit={handleEdit} />
          : undefined
        }
      />
      {isBuyerEditingEnabled && (
        <>
          <Modal
            title={t('buyers:add_buyer')}
            description={t('buyers:add_buyer_hint')}
            size="medium"
            isOpen={isCreateModalOpen}
            onClose={() => setIsCreateModalOpen(false)}
          >
            <BuyerForm isLoading={isCreatingBuyer} onCancel={() => setIsCreateModalOpen(false)} onSubmit={handleCreateBuyerSubmit} />
          </Modal>
          <Modal
            title={t('buyers:update_buyer')}
            description={t('buyers:update_buyer_description')}
            size="medium"
            isOpen={isUpdateModalOpen}
            onClose={() => setIsUpdateModalOpen(false)}
          >
            <BuyerForm
              defaultValues={editedBuyerDefaultValues}
              isLoading={isUpdatingBuyer}
              onCancel={() => setIsUpdateModalOpen(false)}
              onSubmit={handleUpdateBuyerSubmit}
            />
          </Modal>
        </>
      )}
    </>
  );
};
